import React, { useState } from "react";
import { Modal, ModalOverlay, ModalContent, ModalCloseButton, useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { APPRECIATION_TYPES, APPRECIATION_KEYS } from "./appreciations";

export default function AppreciationModal({ isOpen, onClose, recipient, onGiven }) {
  const toast = useToast();
  const [type, setType] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function send() {
    if (!type) { toast({ title: "Pick a badge", status: "warning" }); return; }
    setSubmitting(true);
    try {
      const r = await api(`/appreciations/to/${recipient._id}`, {
        method: "POST",
        body: JSON.stringify({ type, note }),
      });
      toast({ title: "Appreciation sent 🙌", status: "success" });
      onGiven && onGiven(r.appreciation);
      setType(null); setNote("");
      onClose();
    } catch (e) {
      toast({ title: e.message || "Failed", status: "error" });
    } finally { setSubmitting(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalCloseButton />
        <div className="p-6">
          <h2 className="text-lg font-semibold text-ink-800 mb-1">Appreciate {recipient?.username || "teammate"}</h2>
          <p className="text-xs text-ink-500 mb-5">Pick a badge that fits the moment. Notes are optional but encouraged.</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-5">
            {APPRECIATION_KEYS.map((k) => {
              const m = APPRECIATION_TYPES[k];
              const selected = type === k;
              return (
                <button
                  key={k}
                  onClick={() => setType(k)}
                  className={[
                    "p-3 rounded-md border text-center transition cursor-pointer",
                    // Brand tokens, and theme-aware. The old blue-50/blue-400
                    // treatment put m.color on a pale blue that failed AA for
                    // all ten types, and stayed light in dark mode.
                    selected
                      ? "border-[var(--accent-primary)] bg-[var(--accent-soft)] ring-2 ring-[var(--accent-soft)]"
                      : "border-[var(--border-subtle)] hover:border-[var(--text-tertiary)] hover:bg-[var(--bg-subtle)]",
                  ].join(" ")}
                >
                  <div className="text-2xl mb-1">{m.emoji}</div>
                  <div className="text-[11px] font-semibold leading-tight" style={{ color: selected ? "var(--text-primary)" : "var(--text-secondary)" }}>
                    {m.label}
                  </div>
                </button>
              );
            })}
          </div>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a personal note (optional)..."
            rows={3}
            maxLength={500}
            className="w-full text-sm bg-ink-50 rounded-md p-3 border border-transparent focus:border-blue-300 focus:bg-white outline-none resize-y"
          />
          <div className="text-[10px] text-ink-400 text-right mt-1">{note.length}/500</div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ink-600 hover:bg-ink-100 rounded-md">Cancel</button>
            <button
              onClick={send}
              disabled={submitting || !type}
              className={[
                "px-4 py-2 text-sm font-semibold rounded-md text-white transition",
                submitting || !type ? "bg-ink-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600",
              ].join(" ")}
            >
              {submitting ? "Sending..." : "Send appreciation"}
            </button>
          </div>

          <div className="mt-4 text-[11px] text-ink-400">
            Limits: 5 per teammate / 30 total per month, to keep appreciations meaningful.
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
